<template>
    <div class="story-list">
        <div
            id="items"
            class="story-list__items"
        >
            <div class="story-list__item story-list__filters">
                <slot name="filters" />
            </div>

            <template v-if="data?.entries?.length === 0 || pending">
                <BaseCard
                    v-for="placeholder in [1, 2, 3, 4, 5]"
                    :key="placeholder"
                    class="story-list__item base-card--compact"
                    :class="{
                        'story-list__item--empty-state': data?.entries?.length === 0 || !pending
                    }"
                    image-sizes="(min-width: 1440px) 313px, (min-width: 992px) 22vw, (min-width: 768px) 62vw, 80vw"
                >
                    <template #image>
                        <Skeletor
                            :shimmer="pending"
                            height="100%"
                        />
                    </template>

                    <template #header>
                        <Skeletor :shimmer="pending" />
                    </template>

                    <template #description>
                        <Skeletor
                            :shimmer="pending"
                            width="95%"
                        />
                        <Skeletor
                            :shimmer="pending"
                            width="85%"
                        />
                        <Skeletor
                            :shimmer="pending"
                            width="90%"
                        />
                    </template>
                </BaseCard>
            </template>

            <template v-else-if="data?.entries?.length > 0">
                <BaseCard
                    v-for="(story, index) in data?.entries"
                    :key="`item-${index}`"
                    class="story-list__item base-card--compact"
                    :link="'/'+ story?.uri"
                    :images="story?.contentHero?.[0]?.image?.length ? [story?.contentHero?.[0]?.image] : []"
                >
                    <template
                        v-if="story?.contentHero?.[0]?.image?.length"
                        #image
                    >
                        <BaseImage
                            sizes="(max-width: 900px) 64vw, (max-width: 1440px: 25vw, 362px"
                            :image="story.contentHero[0].image[0]"
                            :title="story.contentHero[0].image[0].title"
                            :width="story.contentHero[0].image[0].width"
                            :height="story.contentHero[0].image[0].height"
                            :loading="index === 0 ? 'eager' : 'lazy'"
                            :fetchpriority="index === 0 ? 'high' : 'auto'"
                        />
                    </template>
                    <template #header>
                        <h3>{{ story.title }}</h3>
                    </template>

                    <template #description>
                        {{ story?.contentHero?.[0]?.description }}
                    </template>

                    <template
                        v-if="story?.uri"
                        #link
                    >
                        <BaseArrowLink
                            label-position="left"
                            :element="'div'"
                        >
                            {{ $t('read-more') }}
                        </BaseArrowLink>
                    </template>
                </BaseCard>
            </template>

            <template v-if="data?.entries?.length === 0">
                <div
                    class="story-list__empty-state"
                >
                    <h3>
                        {{ $t('no-stories-found') }}
                    </h3>
                </div>
            </template>
        </div>

        <BasePaginator
            class="story-list__paginator"
            :total-pages="totalPages"
            :root-url="`/stories`"
            :current-page="currentPage"
        />
    </div>
</template>

<script setup>
import StoryListQuery from '~/graphql/queries/storyList.graphql';
const props = defineProps({
    activeFilter: {
        type: String,
        default: null
    }
});
const propsRef = toRefs(props);

const { variables } = useCraftGraphql();
delete variables.uri;
variables.limit = 10;

const route = useRoute();
watchEffect(() => {
    variables.relatedToEntries = [{ slug: toValue(propsRef.activeFilter) }] || [];
    variables.offset = route.params.pageNumber ? (route.params.pageNumber - 1) * variables.limit : 0;
});

const response = await useLazyAsyncQuery({
    query: StoryListQuery,
    variables
});

const pending = response.pending;
const data = response.data;

const currentPage = computed(() => {
    return route.params.pageNumber ? Number(route.params.pageNumber) : 1;
});
const totalPages = computed(() => {
    if (response?.data?.value?.entryCount === undefined) {
        return 0;
    }

    return Math.ceil(response?.data?.value?.entryCount / variables.limit);
});
</script>

<style lang="less">
.story-list {
    --gutter: @max-page-width * .065;

    .base-grid(var(--grid-maxWidth-page), false);
    padding-bottom: var(--grid-gutter-margin);
}

.story-list__items {
    grid-column: first / right;
    margin-left: -6.5vw;
    width: 100%;
    position: relative;

    @media @q-md-max {
        grid-column: first / right-side-gutter;
        .base-grid(var(--grid-maxWidth-page), false);
        row-gap: var(--grid-gutter-margin);
    }

    @media @q-md-min {
        column-count: 2;
    }

    @media @q-lg-min {
        column-count: 3;
    }

    @media ( min-width: @max-page-width ) {
        margin-left: 0 - @max-page-width * .065;
    }
}

.story-list__item {
    break-inside: avoid;
    margin-bottom: 6.5vw;
    transition: opacity var(--transition--timing-medium);

    h3 {
        font-size: var(--font-size-2xl);
    }

    @media @q-md-max {
        .base-card__description {
            font-size: 1rem;
        }

        &:nth-child(odd) {
            grid-column: left-side-gutter / third;
        }

        &:nth-child(even) {
            grid-column: left-gutter / -1;
        }
    }
    @media @q-md-min {
        margin-left: 6.5vw;
        margin-bottom: 6.5vw;

        &:nth-child(3n-2) {
            grid-column: left-side-gutter / left-gutter;
        }

        &:nth-child(3n-1) {
            grid-column: second;
        }

        &:nth-child(3n) {
            grid-column: third / -1;
        }
    }

    .base-card__hoverable-image {
        aspect-ratio: unset;

        figure {
            object-fit: unset;
            width: auto;
            height: auto;
            position: relative;
        }

        .base-image__image {
            display: inline;
        }
    }

    @media ( min-width: @max-page-width ) {
        margin-left: @max-page-width * .065;
        margin-bottom: @max-page-width * .065;
    }
}

.story-list__filters {
    @media @q-md-max {
        margin-inline: calc(0rem - var(--grid-gutter-margin));
        width: calc(100% + (var(--grid-gutter-margin) * 2));
        padding-bottom: var(--grid-gutter-margin);
        box-shadow: 0 1px 0 0 rgba(0, 0, 0, .08);

        &.story-list__item {
            // stylelint-disable-next-line no-important
            grid-column: 1/-1;
        }

        li:first-child {
            padding-left: var(--grid-gutter-margin);
        }

        li:last-child {
            padding-right: var(--grid-gutter-margin);
        }
    }
}

.story-list__paginator {
    grid-column: first / right-side-gutter;
    margin-top: var(--spacing-2xl);

    @media @q-md-min {
        margin-top: var(--spacing-3xl);
        grid-column: left-gutter / third;
    }
}

.story-list__empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;

    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 50%;
    max-height: 60vh;

    pointer-events: none;

    @media @q-md-min {
        left: 6.5vw;
    }
}

.story-list__item--empty-state {
    opacity: .5;
}
</style>
